import { useCallback, type MouseEvent, type PropsWithChildren, type FC } from 'react';
import { Link, type LinkProps, useNavigate } from 'react-router-dom';

type BeforeNavigateFn = () => boolean | Promise<boolean>;

const isModifiedEvent = (event: MouseEvent<HTMLAnchorElement>) =>
  event.metaKey || event.altKey || event.ctrlKey || event.shiftKey || event.button !== 0;

const INTERACTIVE_TARGET_SELECTOR =
  'button, [role=\"button\"], input, select, textarea, option, [data-navigable-link-ignore]';

const isInteractiveTarget = (eventTarget: EventTarget | null): boolean => {
  if (!eventTarget || !(eventTarget instanceof Element)) {
    return false;
  }
  return Boolean(eventTarget.closest(INTERACTIVE_TARGET_SELECTOR));
};

interface NavigableLinkProps extends Omit<LinkProps, 'onClick'> {
  disabled?: boolean;
  beforeNavigate?: BeforeNavigateFn;
  onClick?: LinkProps['onClick'];
}

export const NavigableLink: FC<PropsWithChildren<NavigableLinkProps>> = (props) => {
  const {
    beforeNavigate,
    disabled,
    onClick,
    target,
    rel,
    to,
    replace,
    state,
    className,
    relative,
    preventScrollReset,
    viewTransition,
    ...rest
  } = props;
  const navigate = useNavigate();

  const computedRel = target === '_blank' ? rel ?? 'noopener noreferrer' : rel;
  const navigableLinkClassName = className ? `navigable-link ${className}` : 'navigable-link';

  const handleClickCapture = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      if (event.defaultPrevented) return;

      if (isInteractiveTarget(event.target)) {
        event.preventDefault();
        return;
      }

      if (isModifiedEvent(event)) {
        return;
      }

      if (disabled) {
        event.preventDefault();
        return;
      }

      if (!beforeNavigate) {
        return;
      }

      event.preventDefault();
      const href = (event.currentTarget as HTMLAnchorElement | null)?.href;
      const shouldNavigate = await beforeNavigate();
      if (shouldNavigate === false) {
        return;
      }

      if (target === '_blank') {
        if (href) {
          window.open(href, '_blank', computedRel ?? undefined);
        }
        return;
      }

      navigate(to, { replace, state, relative, preventScrollReset, viewTransition });
    },
    [
      beforeNavigate,
      computedRel,
      disabled,
      navigate,
      onClick,
      preventScrollReset,
      relative,
      replace,
      state,
      target,
      to,
      viewTransition
    ]
  );

  return (
    <Link
      {...rest}
      to={to}
      target={target}
      rel={computedRel}
      replace={replace}
      state={state}
      className={navigableLinkClassName}
      onClickCapture={handleClickCapture}
    >
      {props.children}
    </Link>
  );
};

export default NavigableLink;
